/**
 * Per-venue freshness classification for My Trades (issue #125).
 *
 * Pure + time-injected so threshold transitions and recovery are unit-testable
 * with fake timers. Reuses the evidence the venue data paths already expose —
 * a status enum + a last-success timestamp — rather than introducing a new
 * global feed-health system.
 *
 * "Freshness" here is about the DATA we're showing, so age is measured from the
 * last *successful* fetch, never the last attempt (a failed refresh must not
 * reset the age and make retained data look current). `refreshing` is orthogonal
 * to the state: it just means a fetch is in flight over whatever we already show.
 */

/** Raw status shared by both venues (Poly hook's enum; Kalshi maps onto it). */
export type VenueRawStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export type VenueFreshnessState =
  | 'loading' // first fetch in flight, nothing shown yet
  | 'fresh' // last success recent (within threshold, if any)
  | 'retained' // showing last-known data that isn't fresh (aged past threshold OR a failed refresh)
  | 'unavailable' // venue reports it can't serve (e.g. not connected) — Poly only, via its code
  | 'error'; // failed with no prior success to fall back on

export interface VenueFreshnessInput {
  status: VenueRawStatus;
  /** Epoch ms of the last SUCCESSFUL fetch, or null if none has succeeded. */
  lastSuccessMs: number | null;
  /** Current time — injected for deterministic fake-timer tests. */
  nowMs: number;
  /** Age (ms) past which fresh data becomes retained/stale. `null` disables
   *  age-based staleness (e.g. Kalshi, which has no poll cadence to violate). */
  staleThresholdMs: number | null;
}

export interface VenueFreshness {
  state: VenueFreshnessState;
  /** ms since the last successful fetch, or null if none has succeeded. */
  ageMs: number | null;
  /** A fetch is in flight over the currently-shown data. */
  refreshing: boolean;
}

export function classifyVenueFreshness({
  status,
  lastSuccessMs,
  nowMs,
  staleThresholdMs,
}: VenueFreshnessInput): VenueFreshness {
  const ageMs = lastSuccessMs == null ? null : Math.max(0, nowMs - lastSuccessMs);
  const refreshing = status === 'loading';

  // Never succeeded → nothing to retain; the outcome is loading / unavailable / error.
  if (lastSuccessMs == null) {
    if (status === 'loading' || status === 'idle') return { state: 'loading', ageMs, refreshing };
    if (status === 'unavailable') return { state: 'unavailable', ageMs, refreshing };
    return { state: 'error', ageMs, refreshing };
  }

  // We have last-known-good data, so a failure retains rather than errors.
  if (status === 'unavailable') return { state: 'unavailable', ageMs, refreshing };
  if (status === 'error') return { state: 'retained', ageMs, refreshing };

  // status is 'ready' (or 'loading' refreshing in place): fresh unless aged out.
  if (staleThresholdMs != null && ageMs != null && ageMs >= staleThresholdMs) {
    return { state: 'retained', ageMs, refreshing };
  }
  return { state: 'fresh', ageMs, refreshing };
}

/** Per-Kalshi-surface evidence. Kalshi is fetched as three surfaces together
 *  (realized rows, rejected orders, rebate summary); each carries its own last
 *  success time and last-fetch outcome. */
export interface KalshiSurfaceState {
  /** Realized lifecycle status (drives realized-only UI too). */
  realizedStatus: 'idle' | 'loading' | 'ready' | 'error';
  realizedOkMs: number | null;
  tradesOkMs: number | null;
  tradesLastOk: boolean;
  rebateOkMs: number | null;
  rebateLastOk: boolean;
}

/** Which secondary Kalshi surfaces are currently on screen (realized rows are
 *  always shown). Only a VISIBLE stale surface should mark the venue retained. */
export interface KalshiVisibility {
  rejectedShown: boolean;
  rebateShown: boolean;
}

/**
 * Collapse the per-surface Kalshi evidence into a single venue `status` +
 * `lastSuccessMs` for the freshness classifier (reviewer #158). The venue reads
 * retained when ANY currently-visible surface is showing last-good data after a
 * failed refresh — not only when `/realized` fails — and the age reflects the
 * STALEST visible surface so a partial failure can't look current.
 */
export function kalshiVenueEvidence(
  s: KalshiSurfaceState,
  vis: KalshiVisibility,
): { status: VenueRawStatus; lastSuccessMs: number | null } {
  // Realized never loaded → first-load loading/error, nothing to retain.
  if (s.realizedOkMs == null) {
    return { status: s.realizedStatus === 'error' ? 'error' : 'loading', lastSuccessMs: null };
  }
  const realizedStale = s.realizedStatus === 'error';
  // A currently-visible secondary surface that failed before its first success
  // is still failed evidence. Requiring an old success timestamp here would
  // incorrectly let the successful realized surface make the whole venue look
  // ready while the visible rejected/rebate surface has never loaded.
  const tradesStale = vis.rejectedShown && !s.tradesLastOk;
  const rebateStale = vis.rebateShown && !s.rebateLastOk;
  const status: VenueRawStatus = realizedStale || tradesStale || rebateStale ? 'error' : 'ready';

  const times: number[] = [s.realizedOkMs];
  if (vis.rejectedShown && s.tradesOkMs != null) times.push(s.tradesOkMs);
  if (vis.rebateShown && s.rebateOkMs != null) times.push(s.rebateOkMs);
  return { status, lastSuccessMs: Math.min(...times) };
}

/** Poly polls every 120s; flag stale after ~2 missed polls. */
export const POLY_STALE_THRESHOLD_MS = 240_000;
/** Kalshi has no poll cadence, so no age-based staleness — only a failed
 *  refresh moves it off "fresh". Age is still shown as an informational label. */
export const KALSHI_STALE_THRESHOLD_MS = null;

/** Compact age label for a freshness badge: "Ns" under 2 min, "Nm" under 2 h,
 *  else "Nh" (mirrors Position Tracker's formatSyncAge, extended to hours since
 *  Kalshi data can age indefinitely with no poll). */
export function formatFreshnessAge(ageMs: number): string {
  const s = Math.round(ageMs / 1000);
  if (s < 120) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 120) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}

/** Whether the per-venue badge should be shown. Hidden while fresh or on the
 *  first load (the page's own spinner covers that) — mirrors OrdersSyncBadge,
 *  which stays out of the way until something is actually off. */
export function shouldShowFreshnessBadge(state: VenueFreshnessState): boolean {
  return state === 'retained' || state === 'unavailable' || state === 'error';
}
